/* ==========================================================================
   <promo-code>: copy a discount code, or apply it to the cart in one tap.
   Used by the promo bar, product page, sticky add-to-cart and cart drawer.
   Applies the code with Shopify's cart discount API (same as the cart's own
   discount field) and also hits /discount/CODE so it sticks for checkout
   even when the cart is still empty.
   ========================================================================== */
(function () {
  if (customElements.get('promo-code')) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cartPromise = null;
  var norm = function (c) { return String(c || '').trim().toUpperCase(); };
  var storeKey = function (c) { return 'promo-applied:' + norm(c); };

  function getCart(force) {
    if (!cartPromise || force) {
      cartPromise = fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return cartPromise;
  }

  function findCode(cart, code) {
    if (!cart || !cart.discount_codes) return null;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      if (norm(cart.discount_codes[i].code) === norm(code)) return cart.discount_codes[i];
    }
    return null;
  }

  function remember(code, on) {
    try { on ? sessionStorage.setItem(storeKey(code), '1') : sessionStorage.removeItem(storeKey(code)); } catch (e) {}
  }
  function remembered(code) {
    try { return sessionStorage.getItem(storeKey(code)) === '1'; } catch (e) { return false; }
  }

  function announce(text) {
    var el = document.getElementById('status-announcement');
    if (el) { el.textContent = ''; setTimeout(function () { el.textContent = text; }, 50); }
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy') ? resolve() : reject(); } catch (e) { reject(e); }
      document.body.removeChild(ta);
    });
  }

  var pending = {};
  function applyCode(code) {
    var key = norm(code);
    if (pending[key]) return pending[key];

    pending[key] = getCart(true).then(function (cart) {
      var existing = ((cart && cart.discount_codes) || [])
        .map(function (d) { return d.code; })
        .filter(function (c) { return norm(c) !== key; });

      var cookie = fetch(root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart.js'), { credentials: 'same-origin' })
        .catch(function () {});

      return cookie.then(function () {
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: existing.concat([code]).join(',') })
        });
      });
    }).then(function (res) {
      if (!res.ok) throw new Error('update failed');
      return res.json();
    }).then(function (updated) {
      cartPromise = Promise.resolve(updated);
      var entry = findCode(updated, code);
      var result = 'applied';
      if (updated.item_count > 0 && entry && entry.applicable === false) result = 'not-applicable';
      if (updated.item_count > 0 && !entry) result = 'not-applicable';

      if (result === 'applied') remember(code, true);
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('promo:change', { detail: { code: code, state: result } }));
      return result;
    }).finally(function () {
      delete pending[key];
    });

    return pending[key];
  }

  class PromoCode extends HTMLElement {
    connectedCallback() {
      this.code = this.dataset.code;
      if (!this.code) return;
      this.addEventListener('click', this);
      this._onChange = this._onChange.bind(this);
      document.addEventListener('promo:change', this._onChange);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('promo:change', this._onChange);
    }

    _onChange(e) {
      if (e.detail && norm(e.detail.code) === norm(this.code)) this.setState(e.detail.state);
    }

    sync() {
      var self = this;
      if (remembered(this.code)) this.setState('applied');
      getCart().then(function (cart) {
        if (!cart) return;
        var entry = findCode(cart, self.code);
        if (entry && (entry.applicable !== false || cart.item_count === 0)) {
          self.setState('applied');
          remember(self.code, true);
        } else if (cart.item_count > 0 && !entry && remembered(self.code)) {
          // Code was removed from the cart (e.g. via the cart drawer): offer it again.
          remember(self.code, false);
          self.setState('idle');
        }
      });
    }

    setState(state) {
      var btn = this.querySelector('[data-promo-apply]');
      if (!btn) return;
      var label = btn.querySelector('[data-label]');
      clearTimeout(this._reset);
      this.dataset.state = state;
      btn.removeAttribute('aria-busy');

      if (state === 'applied') {
        label.textContent = this.dataset.labelApplied;
        btn.setAttribute('aria-pressed', 'true');
      } else if (state === 'loading') {
        btn.setAttribute('aria-busy', 'true');
      } else if (state === 'error' || state === 'not-applicable') {
        label.textContent = state === 'error' ? this.dataset.labelError : this.dataset.labelNotApplicable;
        btn.removeAttribute('aria-pressed');
        var self = this;
        this._reset = setTimeout(function () { self.setState('idle'); }, 3000);
      } else {
        label.textContent = this.dataset.labelApply;
        btn.removeAttribute('aria-pressed');
      }
    }

    handleEvent(e) {
      var copyBtn = e.target.closest('[data-promo-copy]');
      var applyBtn = e.target.closest('[data-promo-apply]');
      var self = this;

      if (copyBtn) {
        var text = copyBtn.querySelector('[data-copy-text]');
        copyText(this.code).then(function () {
          var original = self.code;
          text.textContent = self.dataset.labelCopied;
          copyBtn.classList.add('is-copied');
          announce(self.dataset.labelCopied + ' ' + original);
          setTimeout(function () { text.textContent = original; copyBtn.classList.remove('is-copied'); }, 1600);
        }).catch(function () {});
        return;
      }

      if (applyBtn) {
        if (this.dataset.state === 'applied' || this.dataset.state === 'loading') return;
        this.setState('loading');
        applyCode(this.code).then(function (result) {
          self.setState(result);
          if (result === 'applied') announce(self.code + ' : ' + self.dataset.labelApplied);
        }).catch(function () { self.setState('error'); });
      }
    }
  }

  customElements.define('promo-code', PromoCode);
})();

/* ==========================================================================
   <sticky-atc>: sticky add-to-cart bar for product pages.
   Mirrors the main add-to-cart button (label, price, sold-out state) and
   submits the main product form, so the theme's own cart flow runs unchanged.
   ========================================================================== */
(function () {
  if (customElements.get('sticky-atc')) return;

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-atc-button]');
      this.labelEl = this.querySelector('[data-sticky-atc-label]');
      this.section = document.querySelector('main buy-buttons') && document.querySelector('main buy-buttons').closest('.shopify-section');
      if (!this.section || !this.button) return;

      this.button.addEventListener('click', this.onClick.bind(this));
      this.io = new IntersectionObserver(this.onIntersect.bind(this));
      this.bind();
      var self = this;
      this.mo = new MutationObserver(function () {
        clearTimeout(self._t);
        self._t = setTimeout(function () { self.bind(); }, 50);
      });
      this.mo.observe(this.section, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.mo) this.mo.disconnect();
    }

    bind() {
      var target = this.section.querySelector('buy-buttons');
      if (!target) return;
      if (target !== this.target) {
        if (this.target) this.io.unobserve(this.target);
        this.target = target;
        this.io.observe(target);
      }
      var mainBtn = target.querySelector('[type="submit"]');
      var mainLabel = target.querySelector('.buy-buttons__label') || mainBtn;
      if (mainLabel && this.labelEl) this.labelEl.textContent = mainLabel.textContent.trim();
      if (mainBtn) this.button.disabled = mainBtn.disabled;
    }

    onIntersect(entries) {
      var entry = entries[0];
      var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', scrolledPast);
      this.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
      this.toggleAttribute('inert', !scrolledPast);
      document.documentElement.classList.toggle('has-sticky-atc', scrolledPast);
    }

    onClick() {
      var buyButtons = this.section.querySelector('buy-buttons');
      var formId = buyButtons && buyButtons.getAttribute('form');
      var form = (formId && document.getElementById(formId)) || (buyButtons && buyButtons.closest('form'));
      var submit = buyButtons && buyButtons.querySelector('[type="submit"]');
      if (!form || !submit || submit.disabled) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submit);
      } else {
        submit.click();
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();
